import { useCallback, useEffect, useRef } from 'react';

import { useDresser } from '../../../../contexts/DrawerContext';
import { useQuery } from '../../../../contexts/RouterContext';
import { OpenInFullPage, ShareLink } from '../../../generic/Drawer/actions';
import useSnippetUrl from '../hooks/useSnippetUrls';
import type { SnippetsQueryParams } from '../../../../../shared/snippets';

import SnippetsDrawerContent from '.';
import { SNIPPET_DRAWER_SLIDE_DURATION } from './constants';

/**
 * Hook that syncs the snippet drawer with query params.
 * When `selectedSnippetId` is set in the query params, it opens the drawer.
 * When it's cleared, it closes the drawer.
 *
 * @param userRevisionLocator - The user revision locator for the current project
 */
const useSyncSnippetDrawerWithQueryParams = (userRevisionLocator: string) => {
  const [queryParams, setQuery] = useQuery<SnippetsQueryParams>();
  const { replaceDrawer, clearDrawers } = useDresser();
  const snippetUrl = useSnippetUrl(userRevisionLocator);

  // Track the previous selectedSnippetId to detect changes. Initialize to undefined so that
  // when the page first mounts with selectedSnippetId already in the URL, we still treat it
  // as a change (undefined -> id) and open the drawer.
  const prevSelectedSnippetIdRef = useRef<string | undefined>(undefined);

  const onClose = useCallback(() => {
    setQuery({ ...queryParams, selectedSnippetId: undefined });
    clearDrawers();
  }, [queryParams, setQuery, clearDrawers]);

  useEffect(() => {
    const currentSnippetId = queryParams.selectedSnippetId;
    const prevSnippetId = prevSelectedSnippetIdRef.current;

    // Update the ref for next render
    prevSelectedSnippetIdRef.current = currentSnippetId;

    // If snippet ID changed and is now set, open/replace the drawer
    if (currentSnippetId && currentSnippetId !== prevSnippetId) {
      replaceDrawer(
        <SnippetsDrawerContent userRevisionLocator={userRevisionLocator} />,
        {
          onClose,
          slideDuration: SNIPPET_DRAWER_SLIDE_DURATION,
          shouldSkipClickOutside: true,
          buttonsSlot: (
            <>
              <OpenInFullPage url={snippetUrl} />
              <ShareLink url={snippetUrl} tooltipId="share-snippet-link" />
            </>
          ),
        },
      );
    }

    // If snippet ID was cleared, close the drawer
    if (!currentSnippetId && prevSnippetId) {
      clearDrawers();
    }
  }, [queryParams.selectedSnippetId, userRevisionLocator, replaceDrawer, clearDrawers, onClose, snippetUrl]);
};

export default useSyncSnippetDrawerWithQueryParams;
